document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

document.addEventListener('DOMContentLoaded', () => {
  const root = document.documentElement;
  const body = document.body;
  const header = document.querySelector('[data-site-header]');
  const navToggle = document.querySelector('[data-nav-toggle]');
  const nav = document.querySelector('[data-site-nav]');
  const navCloseControls = document.querySelectorAll('[data-nav-close]');
  const revealItems = document.querySelectorAll('[data-reveal]');
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const loader = document.querySelector('[data-site-loader]');
  const loaderBar = loader?.querySelector('[data-loader-progress]');

  if (loader) {
    let loaderProgress = 8;
    let loaderFinished = false;
    const startedAt = Date.now();
    const minVisibleMs = prefersReducedMotion ? 120 : 520;

    const paintLoader = (value) => {
      const clampedValue = Math.max(0, Math.min(100, value));

      if (loaderBar) {
        loaderBar.style.width = `${clampedValue}%`;
      }
    };

    const progressTimer = window.setInterval(() => {
      if (loaderFinished) {
        return;
      }

      if (loaderProgress < 42) {
        loaderProgress += 14;
      } else if (loaderProgress < 74) {
        loaderProgress += 8;
      } else {
        loaderProgress += 4;
      }

      paintLoader(Math.min(loaderProgress, 92));
    }, 85);

    const finishLoader = () => {
      if (loaderFinished) {
        return;
      }

      loaderFinished = true;
      window.clearInterval(progressTimer);

      const elapsed = Date.now() - startedAt;
      const remainingDelay = Math.max(0, minVisibleMs - elapsed);

      window.setTimeout(() => {
        paintLoader(100);
        loader.classList.add('is-complete');

        window.setTimeout(() => {
          loader.classList.add('is-hidden');
          root.classList.remove('is-loading');
          root.classList.add('is-loaded');

          window.setTimeout(() => {
            loader.remove();
          }, prefersReducedMotion ? 0 : 380);
        }, prefersReducedMotion ? 40 : 150);
      }, remainingDelay);
    };

    paintLoader(loaderProgress);

    if (document.readyState === 'complete') {
      finishLoader();
    } else {
      window.addEventListener('load', finishLoader, { once: true });
    }

    window.setTimeout(finishLoader, 2400);
  } else {
    root.classList.remove('is-loading');
    root.classList.add('is-loaded');
  }

  if (navToggle && nav && header) {
    const closeNavigation = () => {
      header.classList.remove('is-open');
      body.classList.remove('menu-open');
      navToggle.setAttribute('aria-expanded', 'false');
      nav.setAttribute('aria-hidden', 'true');
    };

    const openNavigation = () => {
      header.classList.add('is-open');
      body.classList.add('menu-open');
      navToggle.setAttribute('aria-expanded', 'true');
      nav.setAttribute('aria-hidden', 'false');
    };

    navToggle.addEventListener('click', () => {
      const isOpen = header.classList.contains('is-open');

      if (isOpen) {
        closeNavigation();
      } else {
        openNavigation();
      }
    });

    nav.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => {
        window.setTimeout(() => {
          closeNavigation();
        }, 120);
      });
    });

    navCloseControls.forEach((control) => {
      control.addEventListener('click', () => {
        closeNavigation();
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && header.classList.contains('is-open')) {
        closeNavigation();
      }
    });
  }

  if (header) {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateHeaderState = () => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY;

      header.classList.toggle('site-header--elevated', currentScrollY > 12);

      if (!header.classList.contains('is-open') && currentScrollY > 32 && delta > 0.9) {
        header.classList.add('site-header--hidden');
      } else if (delta < -0.04 || currentScrollY <= 12) {
        header.classList.remove('site-header--hidden');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    updateHeaderState();

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(updateHeaderState);
          ticking = true;
        }
      },
      { passive: true }
    );
  }

  const productCarousels = document.querySelectorAll('[data-product-carousel]');

  productCarousels.forEach((carousel) => {
    const slides = Array.from(carousel.querySelectorAll('[data-carousel-slide]'));
    const prevButton = carousel.querySelector('[data-carousel-prev]');
    const nextButton = carousel.querySelector('[data-carousel-next]');

    if (!slides.length) {
      return;
    }

    let activeIndex = slides.findIndex((slide) => slide.dataset.state === 'active');
    activeIndex = activeIndex === -1 ? 0 : activeIndex;

    const totalSlides = slides.length;

    const normalizeIndex = (index) => (index + totalSlides) % totalSlides;

    const getOffset = (index) => {
      let offset = index - activeIndex;

      if (offset > totalSlides / 2) {
        offset -= totalSlides;
      }

      if (offset < totalSlides / -2) {
        offset += totalSlides;
      }

      return offset;
    };

    const renderCarousel = () => {
      slides.forEach((slide, index) => {
        const offset = getOffset(index);
        let state = 'hidden';

        if (offset === 0) {
          state = 'active';
        } else if (offset === -1) {
          state = 'prev';
        } else if (offset === 1) {
          state = 'next';
        } else if (offset === -2) {
          state = 'far-prev';
        } else if (offset === 2) {
          state = 'far-next';
        }

        slide.dataset.state = state;
        slide.setAttribute('aria-hidden', state === 'hidden' ? 'true' : 'false');
      });

      carousel.classList.add('is-ready');
    };

    const moveCarousel = (direction) => {
      activeIndex = normalizeIndex(activeIndex + direction);
      renderCarousel();
    };

    if (totalSlides <= 1) {
      if (prevButton) prevButton.disabled = true;
      if (nextButton) nextButton.disabled = true;
    } else {
      prevButton?.addEventListener('click', () => moveCarousel(-1));
      nextButton?.addEventListener('click', () => moveCarousel(1));

      carousel.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          moveCarousel(-1);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          moveCarousel(1);
        }
      });
    }

    renderCarousel();
  });

  const productForms = document.querySelectorAll('[data-product-form]');

  productForms.forEach((wrapper) => {
    const productDataNode = wrapper.querySelector('[data-product-json]');
    const variantInput = wrapper.querySelector('[data-variant-id]');
    const priceNode = wrapper.closest('.product-page__content')?.querySelector('[data-product-price]');
    const statusNode = wrapper.closest('.product-page__content')?.querySelector('[data-variant-status]');
    const statusPill = wrapper.closest('.product-page__content')?.querySelector('[data-variant-status-pill]');
    const addToCartButton = wrapper.querySelector('[data-add-to-cart]');
    const addToCartLabel = wrapper.querySelector('[data-add-to-cart-label]');
    const optionGroups = Array.from(wrapper.querySelectorAll('.product-option-group'));
    const mainProductBadge = wrapper.closest('.product-page')?.querySelector('[data-main-product-badge]');
    const stickyPriceNode = wrapper.closest('[data-product-page-root]')?.querySelector('[data-sticky-price]');
    const stickyStatusNode = wrapper.closest('[data-product-page-root]')?.querySelector('[data-sticky-status]');
    const stickyActionNode = wrapper.closest('[data-product-page-root]')?.querySelector('[data-sticky-cta]');

    if (!productDataNode || !variantInput || !optionGroups.length) {
      return;
    }

    let productData;

    try {
      productData = JSON.parse(productDataNode.textContent);
    } catch {
      return;
    }

    const getSelectedOptions = () =>
      optionGroups.map((group) => {
        const checkedInput = group.querySelector('input:checked');
        return checkedInput ? checkedInput.value : null;
      });

    const updateVariant = () => {
      const selectedOptions = getSelectedOptions();

      if (selectedOptions.includes(null)) {
        return;
      }

      const matchedVariant = productData.variants.find((variant) =>
        Array.isArray(variant.options) &&
        variant.options.length === selectedOptions.length &&
        variant.options.every((optionValue, index) => optionValue === selectedOptions[index])
      );

      if (!matchedVariant) {
        if (addToCartButton) addToCartButton.disabled = true;
        if (addToCartLabel) addToCartLabel.textContent = 'Nicht verfügbar';
        if (statusNode) statusNode.textContent = 'Variante nicht verfügbar';
        if (statusPill) statusPill.classList.add('product-status-pill--soldout');
        if (mainProductBadge) mainProductBadge.hidden = false;
        return;
      }

      variantInput.value = String(matchedVariant.id);

      if (priceNode) {
        priceNode.textContent = matchedVariant.price;
      }

      if (stickyPriceNode) {
        stickyPriceNode.textContent = matchedVariant.price;
      }

      if (statusNode) {
        statusNode.textContent = matchedVariant.available ? 'Sofort verfügbar' : 'Aktuell ausverkauft';
      }

      if (stickyStatusNode) {
        stickyStatusNode.textContent = matchedVariant.available ? 'Sofort verfügbar' : 'Aktuell ausverkauft';
      }

      if (statusPill) {
        statusPill.classList.toggle('product-status-pill--soldout', !matchedVariant.available);
      }

      if (addToCartButton) {
        addToCartButton.disabled = !matchedVariant.available;
      }

      if (addToCartLabel) {
        addToCartLabel.textContent = matchedVariant.available ? 'In den Warenkorb' : 'Ausverkauft';
      }

      if (stickyActionNode) {
        stickyActionNode.textContent = matchedVariant.available ? 'Größe wählen' : 'Ausverkauft';
        stickyActionNode.classList.toggle('is-disabled', !matchedVariant.available);
      }

      if (mainProductBadge) {
        mainProductBadge.hidden = matchedVariant.available;
      }
    };

    wrapper.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) {
        updateVariant();
      }
    });

    updateVariant();
  });

  const stickyProductRoots = document.querySelectorAll('[data-product-page-root]');

  stickyProductRoots.forEach((productRoot) => {
    const stickyBar = productRoot.querySelector('[data-product-sticky-bar]');
    const purchaseAnchor = productRoot.querySelector('[data-product-purchase-anchor]');

    if (!stickyBar || !purchaseAnchor) {
      return;
    }

    let stickyTicking = false;

    const updateStickyBar = () => {
      const rect = purchaseAnchor.getBoundingClientRect();
      const isPurchaseVisible = rect.top < window.innerHeight - 110 && rect.bottom > 110;
      const shouldShow = window.scrollY > 360 && !isPurchaseVisible;

      stickyBar.hidden = !shouldShow;
      stickyBar.classList.toggle('is-visible', shouldShow);
      stickyTicking = false;
    };

    updateStickyBar();

    window.addEventListener(
      'scroll',
      () => {
        if (!stickyTicking) {
          window.requestAnimationFrame(updateStickyBar);
          stickyTicking = true;
        }
      },
      { passive: true }
    );

    window.addEventListener('resize', updateStickyBar);
  });

  if (prefersReducedMotion) {
    revealItems.forEach((item) => item.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    {
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.12
    }
  );

  revealItems.forEach((item) => observer.observe(item));
});
